import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  RotateCcw,
  Clock,
  Star,
  Crown,
} from "lucide-react";
import { toast } from "sonner";

const SPREADS = [
  { id: "single", name: "Single Card", cards: 1, desc: "Quick insight for a specific question" },
  { id: "three_card", name: "Past / Present / Future", cards: 3, desc: "Understand your timeline and direction" },
  { id: "love", name: "Love Spread", cards: 5, desc: "Deep dive into matters of the heart" },
  { id: "celtic_cross", name: "Celtic Cross", cards: 10, desc: "The most comprehensive life reading" },
];

const TarotPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedSpread, setSelectedSpread] = useState(null);
  const [question, setQuestion] = useState("");
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (showHistory) fetchHistory();
  }, [showHistory]);

  if (!user?.is_premium) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#070B14" }}>
        <div className="text-center max-w-md space-y-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B5CF6] flex items-center justify-center mx-auto">
            <Star className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-display text-3xl text-white">Tarot Readings</h1>
          <p className="text-white/60 font-body">Unlock AI-powered tarot interpretations with Premium.</p>
          <Button data-testid="tarot-upgrade-btn" onClick={() => navigate("/premium")} className="btn-gold text-lg px-8 py-3">
            <Crown className="w-5 h-5 mr-2" /> Upgrade to Premium
          </Button>
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-white/50 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>
    );
  }

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/premium/tarot/history");
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const drawCards = async () => {
    if (!selectedSpread) return;
    setLoading(true);
    setReading(null);
    try {
      const res = await api.post("/premium/tarot/draw", {
        spread_type: selectedSpread.id,
        question,
      });
      setReading(res.data);
      toast.success("Your cards have been revealed!");
    } catch (err) {
      toast.error("Failed to draw cards. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetReading = () => {
    setReading(null);
    setSelectedSpread(null);
    setQuestion("");
  };

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="tarot-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-white/50 hover:text-white p-2" data-testid="tarot-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-display text-xl text-white">Tarot Readings</h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => setShowHistory(!showHistory)}
            className="text-white/50 hover:text-white"
            data-testid="tarot-history-toggle"
          >
            <Clock className="w-4 h-4 mr-2" />
            {showHistory ? "New Reading" : "History"}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {showHistory ? (
          /* History View */
          <div className="space-y-4" data-testid="tarot-history">
            <h2 className="font-display text-2xl text-white">Past Readings</h2>
            {historyLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-white/50 font-body">No readings yet. Draw your first card!</p>
              </div>
            ) : (
              history.map((r) => (
                <div key={r.reading_id} className="glass-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#D4AF37] text-sm font-body capitalize">{r.spread_type.replace("_", " ")} Spread</span>
                    <span className="text-white/30 text-xs font-body">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-white font-display text-lg">{r.interpretation?.overall_theme}</p>
                  {r.question && <p className="text-white/50 text-sm font-body mt-1">Q: {r.question}</p>}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {r.cards?.map((c, i) => (
                      <span key={i} className="px-2 py-1 rounded-lg bg-[#8B5CF6]/10 text-[#C4B5FD] text-xs font-body border border-[#8B5CF6]/20">
                        {c.card} {c.reversed ? "(R)" : ""}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : reading ? (
          /* Reading Result */
          <div className="space-y-6 animate-fade-in-up" data-testid="tarot-reading">
            <div className="text-center space-y-2">
              <h2 className="font-display text-3xl text-white">{reading.interpretation?.overall_theme}</h2>
              <p className="text-[#D4AF37] text-sm font-body capitalize">{reading.spread_type.replace("_", " ")} Spread</p>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {reading.interpretation?.card_interpretations?.map((card, idx) => (
                <div key={idx} className="glass-card p-4 text-center space-y-2 hover:border-[#D4AF37]/30 transition-colors" data-testid={`tarot-card-${idx}`}>
                  <span className="text-[#D4AF37] text-xs uppercase tracking-wider font-body">{card.position}</span>
                  <p className="font-display text-sm text-white">{card.card_name}</p>
                  {card.reversed && <span className="text-red-400 text-xs font-body">Reversed</span>}
                </div>
              ))}
            </div>

            {/* Card Interpretations */}
            <div className="space-y-4">
              {reading.interpretation?.card_interpretations?.map((card, idx) => (
                <div key={idx} className="glass-card p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[#D4AF37] text-sm font-body font-medium">{card.position}:</span>
                    <span className="text-white font-display text-base">{card.card_name}</span>
                    {card.reversed && <span className="text-red-400 text-xs">(Reversed)</span>}
                  </div>
                  <p className="text-white/70 font-body text-sm leading-relaxed">{card.meaning}</p>
                </div>
              ))}
            </div>

            {/* Synthesis */}
            <div className="gold-accent-card p-6">
              <h3 className="font-display text-lg text-white mb-3">Reading Synthesis</h3>
              <p className="text-white/80 font-body leading-relaxed">{reading.interpretation?.synthesis}</p>
            </div>

            {/* Advice */}
            <div className="glass-card p-6 border-l-4 border-[#8B5CF6]">
              <h3 className="font-display text-lg text-white mb-3">Spiritual Advice</h3>
              <p className="text-white/80 font-body leading-relaxed">{reading.interpretation?.advice}</p>
            </div>

            {/* Affirmation */}
            <div className="glass-card p-6 text-center">
              <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
              <p className="font-display text-xl text-white italic">"{reading.interpretation?.affirmation}"</p>
            </div>

            <Button onClick={resetReading} className="btn-gold w-full py-6 text-lg" data-testid="new-reading-btn">
              <RotateCcw className="w-5 h-5 mr-2" /> New Reading
            </Button>
          </div>
        ) : (
          /* Spread Selection */
          <div className="space-y-8 animate-fade-in-up" data-testid="tarot-selection">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#8B5CF6]/20 border border-white/10 flex items-center justify-center mx-auto">
                <Star className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-display text-2xl text-white">Choose Your Spread</h2>
              <p className="text-white/50 font-body text-sm">Select a tarot spread and optionally ask a question</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {SPREADS.map((spread) => (
                <button
                  key={spread.id}
                  data-testid={`spread-${spread.id}`}
                  onClick={() => setSelectedSpread(spread)}
                  className={`text-left p-5 rounded-xl border transition-all ${
                    selectedSpread?.id === spread.id
                      ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 ring-1 ring-[#D4AF37]/20"
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display text-base text-white">{spread.name}</span>
                    <span className="text-[#D4AF37] text-xs font-body">{spread.cards} card{spread.cards > 1 ? "s" : ""}</span>
                  </div>
                  <p className="text-white/50 text-sm font-body">{spread.desc}</p>
                </button>
              ))}
            </div>

            {selectedSpread && (
              <div className="space-y-4 animate-fade-in-up">
                <textarea
                  data-testid="tarot-question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What guidance are you seeking? (optional)"
                  rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50 resize-none"
                />
                <Button
                  data-testid="draw-cards-btn"
                  onClick={drawCards}
                  disabled={loading}
                  className="btn-gold w-full py-6 text-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Drawing your cards...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" /> Draw {selectedSpread.name}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TarotPage;
